import { PE_DEFAULT_LOADING_SKELETON_COUNT } from '@/constants/flights/premiumEconomy'
import type { InterstitialEntry } from '@/redux/slices/interstitialCache'
import type { Card, FareType, SearchData, SrpFareObject, SubTravelOptions } from '@/types/srp'
import { getFaresInfo, getSpecialRtFaresInfo } from '@/utils/srp/helper'

/**
 * Resolves everything the fare-family modal needs to render, from the cached
 * interstitial response layered over the search response.
 *
 * This exists so fare resolution happens in exactly one place. It previously
 * lived in the modal as five independent pieces of rescue logic — a two-map
 * union for special-RT fare ids, a search-time fallback list for the loading
 * window, a "is this really special RT" re-check, a resolvability probe used to
 * gate the fetch, and two separate skeleton-count floors. Each was added to
 * patch a different symptom of the same cause, and each could disagree with the
 * others about whether the modal had data.
 *
 * Layering rule: the cache entry wins where it has data, the search response
 * fills the gaps. Neither is mutated.
 */

/** Local rather than `utils/general/browserHelper.isEmpty`: that module sits on a
 *  large import graph with a known initialisation cycle, and this one is a pure
 *  data transform that should stay cheap to import (and to unit test). */
const hasItems = (value: unknown): boolean => Array.isArray(value) && value.length > 0

export type InterstitialTripMode = 'ow' | 'rt' | 'srt'

export type InterstitialViewStatus =
    /** Nothing cached and no request in flight. */
    | 'idle'
    /** Request in flight and nothing renderable yet — show skeletons. */
    | 'loading'
    /** Renderable. May be search-only data while a request is still running. */
    | 'ready'
    /** Backend returned a price-change / flight-unavailable dialogue. */
    | 'dialogue'
    /** Request failed and there is nothing cached to fall back on. */
    | 'error'

export interface InterstitialLegView {
    travelOptionId: string
    subTravelOptionIds: string[]
    fares: FareType[]
    /** Count to size the strip, so skeletons fill the gap while fares load. */
    totalFareCount: number
    /** True once the full fare family is present, not just the search lead fare. */
    isResolved: boolean
}

export interface InterstitialView {
    status: InterstitialViewStatus
    tripMode: InterstitialTripMode
    /** `isSpecialRT` after confirming the backend actually returned combined fares. */
    isSpecialRT: boolean
    legs: InterstitialLegView[]
    /** Combined whole-trip fares. Empty unless `isSpecialRT`. */
    combinedFares: FareType[]
    combinedFareCount: number
    /** Search data with the cached response layered on, for downstream helpers. */
    resolvedSubTravelOptions: SubTravelOptions
    resolvedFares: SrpFareObject
    /** Every leg (or the combined strip) is showing its full fare family. */
    isResolved: boolean
    /** True when a request should be issued for this combination. */
    needsFetch: boolean
    /** Cached but past its freshness window: paint it, refresh behind it. */
    isStale: boolean
    userDialogue?: any
}

export interface BuildInterstitialViewInput {
    cards: Card[]
    isRT: boolean
    isSpecialRT: boolean
    searchData: Partial<SearchData>
    entry?: InterstitialEntry
    isStale?: boolean
    /** True while this modal has a request in flight. */
    isFetching?: boolean
}

export const getSpecialRtPairKey = (cards: Card[]): string =>
    `[${cards?.[0]?.travelOptionId || ''}, ${cards?.[1]?.travelOptionId || ''}]`

/**
 * Combined special-RT fare ids can arrive by three routes, each of which can
 * hold a different partial set of cabins, so they are unioned rather than
 * preferred in order:
 *   - `specialRTFareMap` (search) — the searched cabin, always present early;
 *   - `travelOptionIdsToFareIdsMap` (search) — same origin, occasionally wider;
 *   - the cached response — the full multi-cabin list, once fetched.
 * Preferring any single source hid whichever cabins only the others carried.
 */
export const collectCombinedFareIds = (
    pairKey: string,
    searchData: Partial<SearchData>,
    entry?: InterstitialEntry
): string[] => {
    const fromSpecialMap = searchData?.specialRTFareMap?.[pairKey]?.fareIds || []
    const fromSearchMap = searchData?.travelOptionIdsToFareIdsMap?.[pairKey] || []
    const fromEntry = entry?.combinedFareIds?.[pairKey] || []
    return Array.from(new Set([...fromSpecialMap, ...fromSearchMap, ...fromEntry].filter(Boolean)))
}

export const buildInterstitialView = ({
    cards,
    isRT,
    isSpecialRT,
    searchData,
    entry,
    isStale = false,
    isFetching = false
}: BuildInterstitialViewInput): InterstitialView => {
    const searchSubTravelOptions = searchData?.subTravelOptions || {}
    const searchFares = searchData?.fares || {}

    // Layer the cached response over search data. A cached subTravelOption that
    // carries no fareIds (a special-RT response describes the trip through its
    // combined map instead) must not shadow the search entry that does.
    const resolvedFares: SrpFareObject = { ...searchFares, ...(entry?.fares || {}) }
    const resolvedSubTravelOptions: SubTravelOptions = { ...searchSubTravelOptions }
    for (const [stoId, cached] of Object.entries(entry?.subTravelOptions || {})) {
        const hasFares = hasItems(cached?.fareIds) || hasItems(cached?.fareList)
        resolvedSubTravelOptions[stoId] = hasFares
            ? cached
            : { ...(searchSubTravelOptions[stoId] || {}), ...cached, fareIds: searchSubTravelOptions[stoId]?.fareIds || [], fareList: searchSubTravelOptions[stoId]?.fareList || [] }
    }

    const pairKey = getSpecialRtPairKey(cards)
    const combinedFareIds = isSpecialRT ? collectCombinedFareIds(pairKey, searchData, entry) : []
    const combinedFares: FareType[] = combinedFareIds.length
        ? getSpecialRtFaresInfo(combinedFareIds, cards[0], resolvedSubTravelOptions, resolvedFares) || []
        : []

    // Trust the caller's classification while the call is in flight, but once a
    // response has landed only keep it if the backend actually returned combined
    // fares. Live traffic shows cards flagged special-RT for which every combined
    // map comes back empty while real per-leg families arrive — rendering those as
    // special RT leaves a permanently empty strip.
    const hasResponse = !!entry && entry.status !== 'loading'
    const effectiveIsSpecialRT = isSpecialRT && (!hasResponse || combinedFareIds.length > 0)

    const legs: InterstitialLegView[] = (cards || []).map(card => {
        const subTravelOptionIds = card?.subTravelOptionIds || []
        const fares: FareType[] = getFaresInfo(card, resolvedSubTravelOptions, resolvedFares) || []
        // `totalFareCount` comes from the search response, so the strip can be
        // sized correctly before the family arrives.
        const declared = subTravelOptionIds.reduce(
            (sum, id) => sum + (resolvedSubTravelOptions[id]?.totalFareCount || 0),
            0
        )
        return {
            travelOptionId: card?.travelOptionId || '',
            subTravelOptionIds,
            fares,
            totalFareCount: Math.max(declared, fares.length),
            isResolved: fares.length > 0 && fares.length >= declared
        }
    })

    const combinedFareCount = effectiveIsSpecialRT
        ? Math.max(searchData?.specialRTFareMap?.[pairKey]?.totalFareCount || 0, combinedFares.length)
        : 0

    const hasRenderableFares = effectiveIsSpecialRT
        ? combinedFares.length > 0
        : legs.length > 0 && legs.every(leg => leg.fares.length > 0)

    const allLegsResolved = effectiveIsSpecialRT
        ? combinedFares.length > 0
        : legs.length > 0 && legs.every(leg => leg.isResolved)

    let status: InterstitialViewStatus = 'idle'
    if (entry?.status === 'dialogue') status = 'dialogue'
    else if (hasRenderableFares) status = 'ready'
    else if (isFetching || entry?.status === 'loading') status = 'loading'
    else if (entry?.status === 'error') status = 'error'

    // Fetch when the cache has no usable answer for this combination, or when a
    // usable answer has gone stale. Never when a request is already in flight.
    const needsFetch = !isFetching && (!entry || entry.status === 'error' || !allLegsResolved || isStale)

    // While loading with nothing to show, size the strips so skeletons render.
    if (status === 'loading') {
        legs.forEach(leg => {
            leg.totalFareCount = leg.totalFareCount || PE_DEFAULT_LOADING_SKELETON_COUNT
        })
    }

    return {
        status,
        tripMode: effectiveIsSpecialRT ? 'srt' : isRT ? 'rt' : 'ow',
        isSpecialRT: effectiveIsSpecialRT,
        legs,
        combinedFares,
        combinedFareCount:
            status === 'loading' ? combinedFareCount || PE_DEFAULT_LOADING_SKELETON_COUNT : combinedFareCount,
        resolvedSubTravelOptions,
        resolvedFares,
        isResolved: allLegsResolved,
        needsFetch,
        isStale,
        userDialogue: entry?.userDialogue
    }
}
